/**
 * Settings Registry - Single Source of Truth
 *
 * This file defines all settings pages in one place. All other files that need
 * settings page information should import from here.
 *
 * To add a new settings page:
 * 1. Add an entry to SETTINGS_PAGES below
 * 2. Create the page component in renderer/pages/settings/
 * 3. Add to SETTINGS_PAGE_COMPONENTS in renderer/pages/settings/settings-pages.ts
 * 4. Add icon to SETTINGS_ICONS in renderer/components/icons/SettingsIcons.tsx
 *
 * That's it - types, routes, and validation are derived automatically.
 */

/**
 * Settings page definition
 */
export interface SettingsPageDefinition {
  /** Unique identifier used in routes and navigation */
  id: string
  /** Display label in settings navigator */
  label: string
  labelKey?: string
  /** Short description shown in settings navigator */
  description: string
  descriptionKey?: string
  /** Hide from settings navigator UI */
  hidden?: boolean
}

/**
 * The canonical list of all settings pages.
 * Order here determines display order in the settings navigator.
 *
 * ADD NEW PAGES HERE - everything else derives from this list.
 */
export const SETTINGS_PAGES = [
  { id: 'app', label: 'App', labelKey: 'settings.page.app.label', description: 'Power, proxy, updates', descriptionKey: 'settings.page.app.description' },
  { id: 'notifications', label: 'Notifications', labelKey: 'settings.page.notifications.label', description: 'Desktop and in-app alerts', descriptionKey: 'settings.page.notifications.description' },
  { id: 'ai', label: 'AI', labelKey: 'settings.page.ai.label', description: 'Model, thinking, connections', descriptionKey: 'settings.page.ai.description' },
  { id: 'appearance', label: 'Appearance', labelKey: 'settings.page.appearance.label', description: 'Theme, font, tool icons', descriptionKey: 'settings.page.appearance.description' },
  { id: 'input', label: 'Input', labelKey: 'settings.page.input.label', description: 'Send key, spell check', descriptionKey: 'settings.page.input.description' },
  { id: 'memory', label: 'Memory', labelKey: 'settings.page.memory.label', description: 'Tell the AI about yourself, your preferences, and project context', descriptionKey: 'settings.page.memory.description' },
  { id: 'workspace', label: 'Workspace', labelKey: 'settings.page.workspace.label', description: 'Name, icon, working directory', descriptionKey: 'settings.page.workspace.description' },
  { id: 'server', label: 'Server', labelKey: 'settings.page.server.label', description: 'Remote access and TLS', descriptionKey: 'settings.page.server.description' },
  { id: 'permissions', label: 'Permissions', labelKey: 'settings.page.permissions.label', description: 'Explore mode rules', descriptionKey: 'settings.page.permissions.description' },
  { id: 'labels', label: 'Labels', labelKey: 'settings.page.labels.label', description: 'Manage session labels', descriptionKey: 'settings.page.labels.description', hidden: true },
  { id: 'shortcuts', label: 'Shortcuts', labelKey: 'settings.page.shortcuts.label', description: 'Keyboard shortcuts', descriptionKey: 'settings.page.shortcuts.description' },
  { id: 'preferences', label: 'Preferences', labelKey: 'settings.page.preferences.label', description: 'User preferences', descriptionKey: 'settings.page.preferences.description', hidden: true },
] as const satisfies readonly SettingsPageDefinition[]

/**
 * Settings subpage type - derived from SETTINGS_PAGES
 * This replaces the manual union type in types.ts
 */
export type SettingsSubpage = (typeof SETTINGS_PAGES)[number]['id']

/**
 * Array of valid settings subpage IDs - for runtime validation
 */
export const VALID_SETTINGS_SUBPAGES: readonly SettingsSubpage[] = SETTINGS_PAGES.map(p => p.id)

/**
 * Type guard to check if a string is a valid settings subpage
 */
export function isValidSettingsSubpage(value: string): value is SettingsSubpage {
  return VALID_SETTINGS_SUBPAGES.includes(value as SettingsSubpage)
}

/**
 * Get settings page definition by ID
 */
export function getSettingsPage(id: SettingsSubpage): SettingsPageDefinition {
  const page = SETTINGS_PAGES.find(p => p.id === id)
  if (!page) throw new Error(`Unknown settings page: ${id}`)
  return page
}
